import React, { useState, useCallback, useRef, useMemo } from 'react';
import {
  View,
  TouchableOpacity,
  StyleSheet,
  Dimensions,
  Keyboard,
  Linking,
  Platform,
} from 'react-native';
import { Text } from '@/components/common/text';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  withTiming,
  interpolate,
  Extrapolation,
  runOnJS,
  Easing,
} from 'react-native-reanimated';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import SystemNavigationBar from 'react-native-system-navigation-bar';
import LinearGradient from 'react-native-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { showConfirm, showAlert } from '@/utils/common/alert';
import { KeyboardDismissWrapper } from '@/components/common/KeyboardDismissWrapper';
import { SVG_COMPONENTS, SVG_DIMENSIONS } from '@/assets/admin';
import { ResioLogo } from '@/assets/ResioLogo';
import { useThemeColors } from '@/hooks/shared/useThemeColors';
import { useMeasuredHeight } from '@/hooks/shared/useMeasuredHeight';
import { REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import {
  ISharedNavigationItem,
  ISharedNavigationGroupConfig,
} from '@/constants/shared/navigation';
import { GlassNavList } from './menu';
import { CanvasEdgeFade, RadialGlowOverlay } from './GradientBackground';

const { width: SCREEN_WIDTH } = Dimensions.get('window');

const MENU_ANIMATION = {
  OPEN_SPRING: { damping: 55, stiffness: 400 },
  CLOSE_DURATION: 200,
  FLING_VELOCITY_THRESHOLD: 500,
  SNAP_BACK_THRESHOLD: 0.3,
};

export type MobileHeaderVariant = 'gradient' | 'image';

export interface MobileHeaderWithGlassMenuProps {
  currentPath: string;
  onNavigate: (path: string) => void;
  onLogoPress?: () => void;
  onSignOut?: () => void;

  navItems: ISharedNavigationItem[];
  navGroupConfig: ISharedNavigationGroupConfig;

  topMenuSlot?: React.ReactNode;
  rightHeaderSlot?: React.ReactNode;
  bottomSlot?: React.ReactNode;

  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;

  variant?: MobileHeaderVariant;
}

export const MobileHeaderWithGlassMenu: React.FC<MobileHeaderWithGlassMenuProps> = ({
  currentPath,
  onNavigate,
  onLogoPress,
  onSignOut,
  navItems,
  navGroupConfig,
  topMenuSlot,
  rightHeaderSlot,
  bottomSlot,
  isOpen: controlledOpen,
  onOpenChange,
  variant = 'gradient',
}) => {
  const { colors } = useThemeColors();
  const insets = useSafeAreaInsets();

  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const isMenuOpen = isControlled ? controlledOpen! : internalOpen;
  const setIsMenuOpen = useCallback(
    (next: boolean) => {
      if (isControlled) {
        onOpenChange?.(next);
      } else {
        setInternalOpen(next);
        onOpenChange?.(next);
      }
    },
    [isControlled, onOpenChange],
  );

  const [animationError, setAnimationError] = useState<string | null>(null);
  const slideX = useSharedValue(SCREEN_WIDTH);

  // The header is a 60pt floor that grows with the OS text size; the menu
  // overlay is positioned below whatever height it actually laid out at.
  const [headerHeight, onHeaderLayout] = useMeasuredHeight(60);
  const totalHeaderHeight = insets.top + headerHeight;

  const menuSlideStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: slideX.value }],
  }));

  const overlayStyle = useAnimatedStyle(() => ({
    opacity: interpolate(
      slideX.value,
      [SCREEN_WIDTH * 0.75, SCREEN_WIDTH],
      [1, 0],
      Extrapolation.CLAMP,
    ),
  }));

  const panGesture = Gesture.Pan()
    .activeOffsetX(10)
    .failOffsetY([-30, 30])
    .onUpdate((e) => {
      'worklet';
      const clamped = Math.min(Math.max(e.translationX, 0), SCREEN_WIDTH);
      slideX.value = clamped;
    })
    .onEnd((e) => {
      'worklet';
      if (
        e.velocityX > MENU_ANIMATION.FLING_VELOCITY_THRESHOLD ||
        e.translationX > SCREEN_WIDTH * MENU_ANIMATION.SNAP_BACK_THRESHOLD
      ) {
        runOnJS(setIsMenuOpen)(false);
        if (Platform.OS === 'android') {
          runOnJS(SystemNavigationBar.setNavigationColor)(colors.bg.base);
        }
        slideX.value = withTiming(
          SCREEN_WIDTH,
          { duration: MENU_ANIMATION.CLOSE_DURATION, easing: Easing.in(Easing.cubic) },
        );
      } else {
        slideX.value = withSpring(0, MENU_ANIMATION.OPEN_SPRING);
      }
    });

  const handleAnimationError = useCallback((error: string) => {
    console.error('Animation error:', error);
    setAnimationError(error);
    setIsMenuOpen(false);
    slideX.value = SCREEN_WIDTH;
  }, [slideX, setIsMenuOpen]);

  const openMenu = useCallback(() => {
    Keyboard.dismiss();
    if (Platform.OS === 'android') {
      SystemNavigationBar.setNavigationColor(colors.bg.base);
    }
    setIsMenuOpen(true);
    slideX.value = withSpring(0, MENU_ANIMATION.OPEN_SPRING);
  }, [slideX, colors, setIsMenuOpen]);

  const closeMenu = useCallback(() => {
    if (Platform.OS === 'android') {
      SystemNavigationBar.setNavigationColor(colors.bg.base);
    }
    setIsMenuOpen(false);
    slideX.value = withTiming(
      SCREEN_WIDTH,
      { duration: MENU_ANIMATION.CLOSE_DURATION, easing: Easing.in(Easing.cubic) },
    );
  }, [slideX, colors, setIsMenuOpen]);

  const toggleMenu = useCallback(() => {
    try {
      if (isMenuOpen) {
        closeMenu();
      } else {
        openMenu();
      }
    } catch (error) {
      handleAnimationError(`Animation failed: ${error}`);
    }
  }, [isMenuOpen, openMenu, closeMenu, handleAnimationError]);

  const closeMenuResolveRef = useRef<(() => void) | null>(null);
  const resolveCloseMenu = useCallback(() => {
    closeMenuResolveRef.current?.();
    closeMenuResolveRef.current = null;
    setIsMenuOpen(false);
  }, [setIsMenuOpen]);

  const closeMenuWithAnimation = useCallback((): Promise<void> => {
    if (Platform.OS === 'android') {
      SystemNavigationBar.setNavigationColor(colors.bg.base);
    }
    setIsMenuOpen(false);
    return new Promise((resolve) => {
      closeMenuResolveRef.current = resolve;
      slideX.value = withTiming(
        SCREEN_WIDTH,
        { duration: MENU_ANIMATION.CLOSE_DURATION, easing: Easing.in(Easing.cubic) },
        () => {
          'worklet';
          runOnJS(resolveCloseMenu)();
        },
      );
    });
  }, [slideX, colors, setIsMenuOpen, resolveCloseMenu]);

  const handleSignOut = useCallback(async () => {
    const confirmed = await showConfirm(
      'Sign Out',
      'Are you sure you want to sign out?',
      'Sign Out',
      'Cancel',
    );
    if (!confirmed) return;
    await closeMenuWithAnimation();
    onSignOut?.();
  }, [closeMenuWithAnimation, onSignOut]);

  const handleNavigation = useCallback(
    (path: string) => {
      try {
        closeMenu();
        onNavigate(path);
      } catch (error) {
        console.error('Navigation error:', error);
        showAlert('Navigation Error', 'Failed to navigate. Please try again.');
      }
    },
    [closeMenu, onNavigate],
  );

  const handleResioLinkPress = useCallback(() => {
    Linking.openURL('https://resio.com');
  }, []);

  const glassButtonStyle = useMemo(
    () =>
      variant === 'image'
        ? [styles.glassButton, { backgroundColor: colors.glass.buttonBgImage, borderColor: colors.glass.buttonBorderImage }]
        : [styles.glassButton, { backgroundColor: colors.glass.buttonBg, borderColor: colors.glass.buttonBorder }],
    [variant, colors.glass],
  );

  if (animationError) {
    return (
      <View style={[styles.safeArea, { paddingTop: insets.top }]}>
        <View style={styles.header}>
          <Text style={[styles.errorText, { color: colors.accent.red }]}>Menu Error: {animationError}</Text>
          <TouchableOpacity
            onPress={() => setAnimationError(null)}
            style={[styles.retryButton, { backgroundColor: colors.text.secondary }]}
          >
            <Text style={[styles.retryButtonText, { color: colors.text.primary }]}>Retry</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <>
      {/* Full-screen gradient backdrop — opacity animated with drawer */}
      <Animated.View style={[styles.menuGradientBackdrop, overlayStyle]} pointerEvents="none">
        <LinearGradient
          colors={[colors.bg.gradientStart, colors.bg.base]}
          style={StyleSheet.absoluteFill}
        />
        <RadialGlowOverlay />
        <CanvasEdgeFade />
      </Animated.View>

      {/* Fixed Header */}
      <View style={[styles.safeArea, { paddingTop: insets.top }]}>
        <KeyboardDismissWrapper style={styles.header} onLayout={onHeaderLayout}>
          <View style={styles.leftContainer}>
            <TouchableOpacity
              style={styles.logoContainer}
              onPress={onLogoPress}
              activeOpacity={0.7}
              accessibilityLabel="Resio logo"
              accessibilityHint="Tap to go to home screen"
              accessibilityRole="button"
            >
              <ResioLogo width={25.5} height={34} />
              <View style={styles.resioNameContainer}>
                <Text style={[styles.resioName, { color: colors.text.secondary }]}>RESIO</Text>
              </View>
            </TouchableOpacity>

            {rightHeaderSlot}
          </View>

          <TouchableOpacity
            style={[styles.menuContainer, glassButtonStyle]}
            onPress={toggleMenu}
            activeOpacity={0.7}
            accessibilityLabel={isMenuOpen ? 'Close menu' : 'Open menu'}
            accessibilityHint={isMenuOpen ? 'Tap to close navigation menu' : 'Tap to open navigation menu'}
            accessibilityRole="button"
            accessibilityState={{ expanded: isMenuOpen }}
          >
            {isMenuOpen ? (
              <SVG_COMPONENTS.CANCEL_X {...SVG_DIMENSIONS.CANCEL_X} color={colors.text.primary} />
            ) : (
              <SVG_COMPONENTS.HAMBURGER {...SVG_DIMENSIONS.HAMBURGER} color={colors.text.primary} />
            )}
          </TouchableOpacity>
        </KeyboardDismissWrapper>
      </View>

      {/* Menu Overlay — slides in from right */}
      <GestureDetector gesture={panGesture}>
        <Animated.View
          style={[
            styles.menuOverlay,
            { top: totalHeaderHeight },
            menuSlideStyle,
          ]}
          pointerEvents={isMenuOpen ? 'auto' : 'none'}
          accessibilityViewIsModal={isMenuOpen}
        >
          <View style={styles.navMenu}>
            {topMenuSlot ? <View style={styles.topMenuSlot}>{topMenuSlot}</View> : null}

            <View style={styles.navListContainer}>
              <GlassNavList
                currentPath={currentPath}
                onNavigate={handleNavigation}
                items={navItems}
                groupConfig={navGroupConfig}
              />
            </View>

            <View
              style={[
                styles.bottomSection,
                { borderTopColor: colors.separator, paddingBottom: insets.bottom },
              ]}
            >
              {bottomSlot}
              <Text style={[styles.learnMoreText, { color: colors.text.subtle }]}>
                Learn more about us at{' '}
                <Text
                  style={[styles.resioLink, { color: colors.text.secondary }]}
                  onPress={handleResioLinkPress}
                >
                  RESIO.COM
                </Text>
              </Text>

              <TouchableOpacity
                style={[
                  styles.signOutButton,
                  { backgroundColor: colors.glass.buttonBg, borderColor: colors.glass.buttonBorder },
                ]}
                onPress={handleSignOut}
                activeOpacity={0.8}
                accessibilityLabel="Sign out"
                accessibilityHint="Tap to sign out of your account"
                accessibilityRole="button"
              >
                <Text style={[styles.signOutText, { color: colors.text.secondary }]} numberOfLines={2}>
                  Sign out
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </Animated.View>
      </GestureDetector>
    </>
  );
};

MobileHeaderWithGlassMenu.displayName = 'MobileHeaderWithGlassMenu';

const styles = StyleSheet.create({
  safeArea: {
    backgroundColor: 'transparent',
    zIndex: 1000,
  },
  header: {
    width: '100%',
    minHeight: 60,
    backgroundColor: 'transparent',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 24,
  },
  leftContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 20,
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  resioNameContainer: {
    flexDirection: 'column',
    gap: 10,
  },
  resioName: {
    fontSize: 18,
    fontWeight: '700',
    letterSpacing: 2,
  },
  glassButton: {
    width: 40,
    height: 40,
    borderWidth: 1,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  menuContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorText: {
    fontSize: 12,
    flex: 1,
  },
  retryButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 4,
  },
  retryButtonText: {
    fontSize: 12,
    fontWeight: '600',
  },
  menuOverlay: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    backgroundColor: 'transparent',
    zIndex: 999,
    flexDirection: 'column',
  },
  navMenu: {
    width: '100%',
    flex: 1,
    backgroundColor: 'transparent',
    paddingTop: 16,
    paddingHorizontal: REDESIGN_SPACING.horizontalPadding,
    paddingBottom: 16,
    justifyContent: 'space-between',
  },
  topMenuSlot: {
    width: '100%',
    paddingBottom: 12,
  },
  navListContainer: {
    flex: 1,
  },
  bottomSection: {
    width: '100%',
    gap: 16,
    paddingTop: 12,
    borderTopWidth: 1,
  },
  learnMoreText: {
    fontFamily: 'System',
    fontWeight: '500',
    fontSize: 14,
    lineHeight: 21,
  },
  resioLink: {
    fontFamily: 'System',
    fontWeight: '600',
    fontSize: 14,
    lineHeight: 21,
  },
  signOutButton: {
    minWidth: 86,
    minHeight: 40,
    borderWidth: 1,
    borderRadius: 12,
    paddingVertical: 8,
    paddingHorizontal: 16,
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'flex-start',
  },
  signOutText: {
    fontFamily: 'System',
    fontWeight: '500',
    fontSize: 14,
    lineHeight: 21,
    textAlign: 'center',
  },
  menuGradientBackdrop: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 998,
  },
});
